import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Chef's Specials — A5 print-ready single-page handout.
 *
 * Pulls items tagged "chef_special" (case-insensitive) and renders them on a
 * single A5 page. Print stylesheet hides chrome, sets @page A5, fits the body
 * to the paper. Designed so a chef can print fresh specials at the start of
 * each service.
 */
export default function ChefSpecialsPrintPage() {
  const { business } = useBusiness();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/menu", { params: { available_only: true } });
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const specials = useMemo(() => {
    return items
      .filter((it) => (it.tags || []).some((t) => (t || "").toLowerCase() === "chef_special"))
      .sort((a, b) => (a.order || 999) - (b.order || 999));
  }, [items]);

  const today = new Intl.DateTimeFormat("en-AU", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  }).format(new Date());

  function printPage() {
    window.print();
  }

  function money(v) {
    if (v == null) return "";
    return Math.round(Number(v)) === Number(v) ? `$${Number(v).toFixed(0)}` : `$${Number(v).toFixed(2)}`;
  }

  return (
    <div data-testid="specials-print-page" className="bg-[#3F3325] min-h-screen specials-root">
      <style>{`
        @media print {
          @page { size: A5; margin: 12mm; }
          .specials-controls, [data-testid="site-header"], [data-testid="site-footer"] { display: none !important; }
          .specials-root, html, body { background: #fff !important; color: #000 !important; }
          .specials-sheet {
            box-shadow: none !important; margin: 0 !important; padding: 0 !important;
            background: white !important; max-width: 100% !important;
            border-radius: 0 !important; border: none !important;
          }
          body { font-size: 10.5pt; }
        }
        .specials-sheet { font-family: Georgia, 'Times New Roman', serif; }
      `}</style>

      <div className="max-w-[148mm] mx-auto py-8 px-3">
        <div className="specials-controls flex items-center justify-between mb-5 text-white">
          <Link to="/menu" data-testid="specials-back" className="inline-flex items-center text-sm hover:text-[#E8B4A2]">
            <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Back to menu
          </Link>
          <Button
            data-testid="specials-print"
            onClick={printPage}
            variant="outline"
            className="rounded-full bg-white text-[#3F3325] border-white"
          >
            <Printer className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Print A5
          </Button>
        </div>

        {/* A5 paper sheet — 148mm wide × 210mm tall */}
        <article
          data-testid="specials-sheet"
          className="specials-sheet bg-[#FAF6EE] rounded-sm shadow-2xl border border-[#D6CBB1] mx-auto px-8 py-10"
          style={{ width: "148mm", minHeight: "210mm" }}
        >
          {/* Header */}
          <header className="text-center border-b-2 border-double border-[#3F3325] pb-4">
            <p className="text-[9pt] uppercase tracking-[0.4em] text-[#9C7B3C]">
              Tonight at
            </p>
            <h1 className="font-serif-display text-[22pt] leading-tight mt-1 text-[#1A1410]">
              {business?.business_name || "Our restaurant"}
            </h1>
            <p className="text-[8.5pt] italic text-[#5C4D33] mt-1">{today}</p>
          </header>

          {/* Title */}
          <div className="text-center mt-7 mb-5">
            <Sparkles className="h-5 w-5 text-[#9C7B3C] inline-block" strokeWidth={1} />
            <p className="font-serif-display text-[26pt] tracking-wide text-[#1A1410] mt-1">
              Chef&apos;s Specials
            </p>
            <p className="text-[8pt] italic text-[#5C4D33] mt-0.5">
              Curated by the kitchen · available while they last
            </p>
          </div>

          {/* Items */}
          {loading && (
            <p data-testid="specials-loading" className="text-center text-[#5C4D33] my-10">Loading…</p>
          )}
          {!loading && specials.length === 0 && (
            <p data-testid="specials-empty" className="text-center text-[#5C4D33] italic my-10">
              No specials marked as Chef&apos;s — tag any dish with <code>chef_special</code> from admin to see it here.
            </p>
          )}
          {!loading && specials.length > 0 && (
            <ul data-testid="specials-list" className="space-y-5">
              {specials.map((it) => (
                <li
                  key={it.id}
                  data-testid={`specials-item-${it.id}`}
                  className="border-b border-dotted border-[#D6CBB1] pb-3 last:border-b-0"
                >
                  <div className="flex items-baseline gap-3">
                    <h3 className="font-serif-display text-[14pt] text-[#1A1410] flex-1">
                      {it.name}
                    </h3>
                    <span className="font-serif-display text-[13pt] text-[#1A1410]">
                      {it.is_priced === false ? "—" : money(it.price)}
                    </span>
                  </div>
                  {it.description && (
                    <p className="text-[9pt] italic text-[#5C4D33] mt-1 leading-snug">
                      {it.description}
                    </p>
                  )}
                  {it.origin && (
                    <p className="text-[8pt] uppercase tracking-[0.18em] text-[#9C7B3C] mt-1">
                      {it.origin}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Footer */}
          <footer className="mt-10 pt-3 border-t border-[#D6CBB1] text-center">
            <p className="text-[7.5pt] text-[#5C4D33] italic">
              Allergens are approximations — please tell your server about severe allergies. Prices include GST.
            </p>
            {business?.tagline && (
              <p className="text-[8pt] mt-2 text-[#3F3325]">{business.tagline}</p>
            )}
          </footer>
        </article>
      </div>
    </div>
  );
}
